const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();

/** Cached `Intl.DateTimeFormat` — construction is expensive in per-row render paths. */
export function getDateTimeFormat(
  locale: string | undefined,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale ?? ''}|${JSON.stringify(options)}`;
  let format = dateTimeFormats.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, options);
    dateTimeFormats.set(key, format);
  }
  return format;
}

/**
 * Parse API event timestamps as UTC.
 * Timezone-less ISO strings (e.g. `2026-08-25T16:00:00`) are treated as UTC;
 * values that already include `Z` or an offset are left unchanged.
 */
export function parseApiUtcDate(value: string): Date {
  const trimmed = value.trim();
  const aspNet = /\/Date\((-?\d+)(?:[+-]\d+)?\)\//.exec(trimmed);
  if (aspNet?.[1]) {
    return new Date(Number(aspNet[1]));
  }
  const hasOffset = /([zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed);
  return new Date(hasOffset ? trimmed : `${trimmed}Z`);
}

/**
 * Format an API UTC timestamp in the device timezone (same approach as Sports
 * kickoff labels via {@link parseApiUtcDate} + `Intl.DateTimeFormat`).
 * Example: `05 Oct 2026 19:29` (en, UTC+2).
 */
export function formatApiDateTime(
  value: string | number | Date | null | undefined,
  locale?: string,
): string {
  if (value == null || value === '') return '';

  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'number') {
    date = new Date(value);
  } else {
    date = parseApiUtcDate(value);
  }
  if (Number.isNaN(date.getTime())) {
    return typeof value === 'string' ? value : '';
  }

  const resolved = locale || undefined;
  const dayMonthYear = getDateTimeFormat(resolved, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
  const time = getDateTimeFormat(resolved, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);

  // Some locales insert a comma (`05 Oct 2026,` / `Oct 05, 2026`); keep a compact label.
  return `${dayMonthYear.replace(/,/g, '')} ${time}`;
}

/**
 * Compact search-result kickoff: `Sat 14 Sep · 20:45` (device timezone).
 */
export function formatSearchEventDate(
  value: string | number | Date | null | undefined,
  locale?: string,
): string {
  if (value == null || value === '') return '';

  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'number') {
    date = new Date(value);
  } else {
    date = parseApiUtcDate(value);
  }
  if (Number.isNaN(date.getTime())) {
    return typeof value === 'string' ? value : '';
  }

  const resolved = locale || undefined;
  const weekdayDayMonth = getDateTimeFormat(resolved, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(date);
  const time = getDateTimeFormat(resolved, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);

  return `${weekdayDayMonth.replace(/,/g, '')} · ${time}`;
}
